import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import TxTable from "@/components/TxTable";
import { AddrLink, Card, CopyBtn, DetailRow, ErrorState, Pager, SkeletonRows, usePaged } from "@/components/common";
import { fmtDate, fmtNum, timeAgo } from "@/lib/format";
import { usePageTitle } from "@/lib/seo";

export default function BlockPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [block, setBlock] = useState(null);
  const [err, setErr] = useState(false);
  usePageTitle(`Block #${id}`, `Transactions, gas usage and producer details for block ${id} on Robinhood Chain.`);
  const pager = usePaged((p) => api.get(`/blocks/${id}/transactions`, { params: p }).then((r) => r.data), [id]);

  useEffect(() => {
    setBlock(null); setErr(false);
    api.get(`/blocks/${id}`).then((r) => setBlock(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <ErrorState message="We could not find this block on Robinhood Chain. Please check the value and try again." />;
  if (!block) return <SkeletonRows n={8} h="h-14" />;

  return (
    <div className="space-y-6 pt-8">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="block-title">Block #{block.height}</h1>
        <div className="flex items-center gap-2 ml-auto">
          <button data-testid="prev-block-btn" onClick={() => navigate(`/block/${block.height - 1}`)} disabled={block.height <= 0}
            className="h-8 px-3 rounded-lg border flex items-center gap-1 text-xs hover:bg-muted transition-colors duration-150 disabled:opacity-30">
            <ChevronLeft size={13} /> Prev
          </button>
          <button data-testid="next-block-btn" onClick={() => navigate(`/block/${block.height + 1}`)}
            className="h-8 px-3 rounded-lg border flex items-center gap-1 text-xs hover:bg-muted transition-colors duration-150">
            Next <ChevronRight size={13} />
          </button>
        </div>
      </div>

      <Card className="px-4 sm:px-5">
        <DetailRow label="Block Height" testId="block-height">{fmtNum(block.height, { maximumFractionDigits: 0 })}</DetailRow>
        <DetailRow label="Timestamp">{fmtDate(block.timestamp)} <span className="text-xs text-muted-foreground">({timeAgo(block.timestamp)})</span></DetailRow>
        <DetailRow label="Transactions">{block.transactions_count ?? 0} transactions in this block</DetailRow>
        <DetailRow label="Block Producer"><AddrLink hash={block.miner?.hash} chars={10} label={block.miner?.hash?.toLowerCase() === "0xa4b000000000000000000073657175656e636572" ? "Robinhood Chain Sequencer" : undefined} /></DetailRow>
        <DetailRow label="Gas Used">{fmtNum(block.gas_used, { maximumFractionDigits: 0 })}</DetailRow>
        <DetailRow label="Block Hash"><span className="inline-flex items-center gap-2 font-mono text-xs break-all">{block.hash} <CopyBtn text={block.hash} /></span></DetailRow>
        <DetailRow label="Parent Hash">
          <button onClick={() => navigate(`/block/${block.height - 1}`)} className="font-mono text-xs text-primary hover:underline break-all text-left">{block.parent_hash}</button>
        </DetailRow>
        <DetailRow label="Base Fee">{block.base_fee_per_gas ? `${(Number(block.base_fee_per_gas) / 1e9).toFixed(4)} gwei` : "—"}</DetailRow>
      </Card>

      <Card>
        <h2 className="px-4 pt-4 pb-2 text-base font-semibold">Transactions in this Block</h2>
        <TxTable items={pager.items} loading={pager.loading} error={pager.error} />
        {!pager.loading && pager.items.length > 0 && <Pager pager={pager} />}
      </Card>
    </div>
  );
}
